
import { Record } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { record_type, list_type, int32_type } from "../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { ofList, empty, tryFind } from "../fable_modules/fable-library-js.5.13.0/Map.js";
import { fold, mapIndexed, length, map, isEmpty } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { compareArrays } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { FSharpResult$2 } from "../fable_modules/fable-library-js.5.13.0/Result.js";
import { PromiseBuilder__Delay_62FBFDE1, PromiseBuilder__Run_212F1D4B } from "../fable_modules/Fable.Promise.3.1.3/Promise.fs.js";
import { promise } from "../fable_modules/Fable.Promise.3.1.3/PromiseImpl.fs.js";
import { errorOfFrame, simRead } from "../Interface/SidecarClient.js";
import { fromZero, fromUInt32, op_LeftShift, op_Addition } from "../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { item } from "../fable_modules/fable-library-js.5.13.0/Array.js";
import { toList } from "../fable_modules/fable-library-js.5.13.0/Seq.js";
import { rangeDouble } from "../fable_modules/fable-library-js.5.13.0/Range.js";

/**
 * One thing the panel shows: a component output, named the way the sidecar names it.
 */
export class PanelSignal extends Record {
    constructor(Comp, Path, Port) {
        super();
        this.Comp = (Comp | 0);
        this.Path = Path;
        this.Port = (Port | 0);
    }
}

export function PanelSignal_$reflection() {
    return record_type("StepPanelData.PanelSignal", [], PanelSignal, () => [["Comp", int32_type], ["Path", list_type(int32_type)], ["Port", int32_type]]);
}

let snapshot = undefined;

/**
 * Drop what was fetched. Called when the simulation ends, the design changes, or the simulator
 * is switched - anything that makes the cycle this was of no longer the cycle being shown.
 */
export function forget() {
    snapshot = undefined;
}

/**
 * The cycle the held values are of, or None when nothing has been fetched.
 */
export function cycleHeld() {
    const option_1 = snapshot;
    if (option_1 != null) {
        return option_1[1];
    }
    else {
        return undefined;
    }
}

/**
 * The value of one signal, if this snapshot is of the cycle asked for.
 * 
 * `None` is "not fetched", which is a different thing from a value of zero and only this can tell
 * them apart. What the panel then DRAWS for one is zero all the same - see
 * `SimulationView.panelValue`, which argues for it: a row appearing and disappearing as replies
 * land is worse to read than a value a moment out of date, and an unread port already looks like
 * this. The distinction is kept here rather than thrown away because the decision is the
 * caller's, and a caller that wants to make the other one has something to make it from.
 */
export function valueAt(cycle, signal) {
    let matchResult, held_1, values_1;
    if (snapshot != null) {
        if ((snapshot[2], snapshot[1] === cycle)) {
            matchResult = 0;
            held_1 = snapshot[1];
            values_1 = snapshot[2];
        }
        else {
            matchResult = 1;
        }
    }
    else {
        matchResult = 1;
    }
    switch (matchResult) {
        case 0:
            return tryFind([signal.Comp, signal.Path, signal.Port], values_1);
        default:
            return undefined;
    }
}

/**
 * Read every signal the panel shows, at one cycle, in one request.
 * 
 * One request and not one per row: the panel is redrawn on every render and has a row per
 * top-level input, output, viewer and register, which on a large design is hundreds. `simRead`
 * takes a list, so this is a single round trip of a few hundred bytes.
 */
export function fill(epoch, cycle, signals) {
    if (isEmpty(signals)) {
        snapshot = [epoch, cycle, empty({
            Compare: (x, y) => (compareArrays(x, y) | 0),
        })];
        return Promise.resolve(new FSharpResult$2(/* Ok */ 0, [undefined]));
    }
    else {
        const requested = map((s) => [s.Comp, s.Port, s.Path], signals);
        return PromiseBuilder__Run_212F1D4B(promise, PromiseBuilder__Delay_62FBFDE1(promise, () => (simRead(epoch, cycle, 1, 1, requested).then((_arg) => {
            const frame = _arg;
            const matchValue = errorOfFrame(frame);
            if (matchValue == null) {
                const wordsPerSample = (new DataView(frame.buffer, frame.byteOffset).getUint32(16, true)) | 0;
                const data = new Uint32Array(frame.buffer, frame.byteOffset + 24, (length(requested) * wordsPerSample));
                snapshot = [epoch, cycle, ofList(mapIndexed((row_1, s_1) => [[s_1.Comp, s_1.Path, s_1.Port], fold((acc, w) => op_Addition(op_LeftShift(acc, 32), fromUInt32(item((row_1 * wordsPerSample) + w, data))), fromZero(), toList(rangeDouble(wordsPerSample - 1, -1, 0)))], signals), {
                    Compare: (x_1, y_1) => (compareArrays(x_1, y_1) | 0),
                })];
                return Promise.resolve(new FSharpResult$2(/* Ok */ 0, [undefined]));
            }
            else {
                const e = matchValue;
                return Promise.resolve(new FSharpResult$2(/* Error */ 1, [e]));
            }
        }))));
    }
}

